import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	BulkCreateHoldingEventDto,
	HoldingEventRes,
	HoldingEventType,
} from '@dealstack/shared/lib/generated';
import { useMutation } from '@tanstack/react-query';
import {
	createBulkHoldingsEvents,
	createHoldingsEvent,
	updateHoldingsEvent,
} from '@dealstack/frontend/app/services/holdingsEvent.service.ts';
import { ComboboxItem } from '@mantine/core';
import { getPersonsLabelsWithIdsForSelect } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils.ts';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { RegularViewTableItemData } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import {
	BulkCreateHoldingsEventFormDto,
	BulkCreateHoldingsEventFormShape,
} from '@dealstack/shared/lib/common/zod/domain/holdings/holdings.zod.ts';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { cleanFileName } from '@dealstack/shared/lib/common/file/file';

export enum BULK_HOLDING_EVENT_FORM_FIELD {
	date = 'date',
	type = 'type',
	note = 'note',
	pledgeToPersonId = 'pledgeToPersonId',
	pledgeToString = 'pledgeToString',
	pledgeFromHolderId = 'pledgeFromHolderId',
	pledgeFromSecurityId = 'pledgeFromSecurityId',
	pledgeFromString = 'pledgeFromString',
	assignedToPersonId = 'assignedToPersonId',
	assignedToString = 'assignedToString',
	documentUrl = 'documentUrl',
	documentName = 'documentName',
	fileId = 'fileId',
	documentMd5 = 'documentMd5',
}

export const useBulkHoldingEventDrawerController = (
	selectedRows: RegularViewTableItemData[],
	refreshHoldingsList: () => void,
	onClose: () => void,
	onSuccess?: () => void
) => {
	const tCapManage = useTranslationCapManage();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);

	const { dealId } = useContext(DealContext);
	const { persons } = useContext(CapManageControllerContext);

	const createBulkHoldingsEventRequest = useMutation({
		mutationFn: createBulkHoldingsEvents,
	});
	const createSingleHoldingEventRequest = useMutation({
		mutationFn: createHoldingsEvent,
	});
	const updateHoldingEventRequest = useMutation({
		mutationFn: updateHoldingsEvent,
	});

	const entitiesSelectOptions: ComboboxItem[] = useMemo(
		() => getPersonsLabelsWithIdsForSelect(persons),
		[persons]
	);

	const DEFAULT_FORM_VALUES = useMemo(() => {
		return {
			[BULK_HOLDING_EVENT_FORM_FIELD.date]: null,
			[BULK_HOLDING_EVENT_FORM_FIELD.type]: HoldingEventType.ISSUANCE_OF_SHARE_CERTIFICATES,
			[BULK_HOLDING_EVENT_FORM_FIELD.note]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.pledgeToPersonId]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.pledgeToString]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.pledgeFromHolderId]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.pledgeFromSecurityId]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.pledgeFromString]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.assignedToPersonId]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.assignedToString]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.documentUrl]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.documentName]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.fileId]: undefined,
			[BULK_HOLDING_EVENT_FORM_FIELD.documentMd5]: undefined,
		} as unknown as BulkCreateHoldingsEventFormDto;
	}, []);

	const bulkHoldingsEventForm = useForm<BulkCreateHoldingsEventFormDto>({
		mode: 'all',
		resolver: zodResolver(BulkCreateHoldingsEventFormShape),
		defaultValues: DEFAULT_FORM_VALUES,
		shouldFocusError: true,
	});

	const removeFile = () => {
		setUploadedFile(null);
		bulkHoldingsEventForm.setValue(BULK_HOLDING_EVENT_FORM_FIELD.documentUrl, undefined);
		bulkHoldingsEventForm.setValue(BULK_HOLDING_EVENT_FORM_FIELD.documentName, undefined);
		bulkHoldingsEventForm.setValue(BULK_HOLDING_EVENT_FORM_FIELD.fileId, undefined);
	};

	const uploadDocument = async (
		eventId: string
	): Promise<{ documentUrl?: string; documentName?: string; documentMd5?: string }> => {
		if (!uploadedFile || !dealId || !eventId) {
			return {};
		}

		try {
			const entityMap = {
				HoldingEvent: eventId,
			};

			const fileData = [
				{
					fileName: cleanFileName(uploadedFile.name),
					mimeType: uploadedFile.type,
					extension: DocumentService.getFileExtensionFromName(uploadedFile.name),
				},
			];

			const preSignedUrl = await DocumentService.createPreSignedUrls({
				fileData,
				entityMap,
				dealId,
			});

			if (preSignedUrl && preSignedUrl.length > 0) {
				const fileInfo = preSignedUrl[0];

				const md5 = await DocumentService.putUploadToPreSignedUrl({
					preSignedUrl: fileInfo.uploadPreSignedUrl,
					file: uploadedFile,
				});

				return {
					documentUrl: fileInfo.fileKey,
					documentName: fileInfo.fileName,
					documentMd5: md5,
				};
			}

			return {};
		} catch (error) {
			showToastV2({
				title: tCapManage('holdings.event.document_failed'),
				state: 'error',
			});
			throw error;
		}
	};

	const onSubmit = async (formValues: BulkCreateHoldingsEventFormDto) => {
		if (!selectedRows || selectedRows.length === 0) {
			return;
		}

		const validRows = selectedRows.filter((row) => row && row.holderId && row.securityId);

		setIsSubmitting(true);

		try {
			const holdings = validRows.map((row) => ({
				holderId: row.holderId,
				securityId: row.securityId,
			}));

			const bulkPayload: BulkCreateHoldingEventDto = {
				date: formValues[BULK_HOLDING_EVENT_FORM_FIELD.date],
				type: formValues[BULK_HOLDING_EVENT_FORM_FIELD.type],
				holdings,
			};

			const fieldsToCopy = [
				'note',
				'pledgeToPersonId',
				'pledgeToString',
				'pledgeFromHolderId',
				'pledgeFromSecurityId',
				'pledgeFromString',
				'assignedToPersonId',
				'assignedToString',
			] as const;

			fieldsToCopy.forEach((field) => {
				const key = BULK_HOLDING_EVENT_FORM_FIELD[field];
				if (formValues[key]) {
					bulkPayload[field] = formValues[key];
				}
			});

			if (uploadedFile) {
				if (holdings.length > 0) {
					try {
						const singleEventPayload = {
							date: bulkPayload.date,
							type: bulkPayload.type,
							holderId: holdings[0].holderId,
							securityId: holdings[0].securityId,
						};

						fieldsToCopy.forEach((field) => {
							if (bulkPayload[field] !== undefined) {
								singleEventPayload[field] = bulkPayload[field];
							}
						});

						const firstHoldingEvent =
							(await createSingleHoldingEventRequest.mutateAsync(
								singleEventPayload
							)) as HoldingEventRes;

						if (firstHoldingEvent && firstHoldingEvent.id) {
							const documentInfo = await uploadDocument(firstHoldingEvent.id);

							if (documentInfo.documentUrl && documentInfo.documentName) {
								await updateHoldingEventRequest.mutateAsync({
									id: firstHoldingEvent.id,
									documentUrl: documentInfo.documentUrl,
									documentName: documentInfo.documentName,
									documentMd5: documentInfo.documentMd5,
								});

								const remainingHoldings = holdings.slice(1);

								if (remainingHoldings.length > 0) {
									const remainingPayload = {
										...bulkPayload,
										holdings: remainingHoldings,
										documentUrl: documentInfo.documentUrl,
										documentName: documentInfo.documentName,
										documentMd5: documentInfo.documentMd5,
									};

									await createBulkHoldingsEventRequest.mutateAsync(
										remainingPayload
									);
								}
							}
						}
					} catch {
						await createBulkHoldingsEventRequest.mutateAsync(bulkPayload);
					}
				}
			} else {
				await createBulkHoldingsEventRequest.mutateAsync(bulkPayload);
			}

			showToastV2({
				message: tCapManage('holdings.event.bulk_add_success'),
			});

			refreshHoldingsList();
			onSuccess?.();
			onClose();
		} catch {
			showToastV2({
				title: tCapManage('holdings.event.bulk_add_fail'),
				state: 'error',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const resetForm = useCallback(() => {
		bulkHoldingsEventForm.reset(DEFAULT_FORM_VALUES);
		removeFile();
	}, [bulkHoldingsEventForm, DEFAULT_FORM_VALUES, removeFile]);

	return {
		bulkHoldingsEventForm,
		createBulkHoldingsEventRequest,
		isSubmitting,
		entitiesSelectOptions,
		onSubmit,
		resetForm,
		BULK_HOLDING_EVENT_FORM_FIELD,
		uploadedFile,
		setUploadedFile,
		removeFile,
	};
};
